import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Clock, LayoutGrid, List as ListIcon } from 'lucide-react';
import { AudioFile } from '../types';
import { audioService } from '../services/api';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { useFavorites } from '../context/FavoritesContext';
import { useToast } from '../context/ToastContext';
import NaatCard from '../components/NaatCard';
import SEO from '../components/SEO';
import './RecentNaatsPage.css';

const RecentNaatsPage: React.FC = () => {
  const { page: pageParam } = useParams<{ page?: string }>();
  const navigate = useNavigate();
  
  const [recentAudios, setRecentAudios] = useState<AudioFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');
  const [totalCount, setTotalCount] = useState(0);
  
  // Get page from URL path or default to 1
  const currentPage = pageParam ? parseInt(pageParam, 10) : 1;
  const pageSize = 15; // Fixed page size for cleaner URLs
  
  const { state, playAudio, addToQueue, togglePlay } = useAudioPlayer();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { showToast } = useToast();

  const MAX_NAATS = 100; // Restrict to maximum 100 recent naats
  const effectiveTotalCount = Math.min(totalCount, MAX_NAATS);
  const TOTAL_PAGES = Math.ceil(effectiveTotalCount / pageSize);

  // Use the current page data directly from API
  const currentPageData = recentAudios;
  const startIndex = (currentPage - 1) * pageSize;

  useEffect(() => {
    const fetchRecentAudios = async () => {
      setLoading(true);
      try {
        // Restrict to first 100 recent naats by checking if we're within limit
        const maxPageForLimit = Math.ceil(MAX_NAATS / pageSize);
        if (currentPage > maxPageForLimit) {
          setRecentAudios([]);
          setTotalCount(0);
          return;
        }

        // Fetch paginated data for current page only
        const response = await audioService.getRecentPaginated(currentPage, pageSize);
        if (response.success && response.data) {
          setRecentAudios(response.data);
          // Ensure total count doesn't exceed our maximum
          setTotalCount(Math.min(response.total || 0, MAX_NAATS));
        } else {
          setRecentAudios([]);
          setTotalCount(0);
        }
      } catch (error) {
        setRecentAudios([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentAudios();
  }, [currentPage, pageSize]);

  const handlePlayAudio = (audio: AudioFile) => {
    // If this audio is already playing, just toggle play/pause
    if (state.currentAudio?.id === audio.id) {
      togglePlay();
    } else {
      playAudio(audio);
    }
  };

  const handleAddToQueue = (audio: AudioFile) => {
    const added = addToQueue(audio);
    if (added) {
      showToast(`Added "${audio.title}" to queue`, 'success', 3000, 'bottom-right');
    } else {
      showToast(`"${audio.title}" is already in queue`, 'info', 3000, 'bottom-right');
    }
  };

  const handleToggleFavorite = (audio: AudioFile) => {
    const wasAlreadyFavorite = isFavorite(audio.id);
    toggleFavorite(audio);
    if (wasAlreadyFavorite) {
      showToast(`Removed "${audio.title}" from favorites`, 'info', 3000, 'top-right-favorites');
    } else {
      showToast(`Added "${audio.title}" to favorites`, 'success', 3000, 'top-right-favorites');
    }
  };

  const handleDownloadComplete = (audio: AudioFile, success: boolean) => {
    if (success) {
      showToast(`Downloaded "${audio.title}" successfully! 🎵`, 'success', 4000, 'bottom-right');
    } else {
      showToast(`Failed to download "${audio.title}". Please try again.`, 'error', 4000, 'bottom-right');
    }
  };

  const handleLayoutChange = (mode: 'grid' | 'list') => {
    setLayout(mode);
  };

  const handlePageChange = (page: number) => {
    // Prevent navigation beyond maximum allowed pages
    const maxAllowedPage = Math.ceil(MAX_NAATS / pageSize);
    if (page > maxAllowedPage) {
      return;
    }

    // Navigate to new page URL
    if (page === 1) {
      navigate('/recent', { replace: true });
    } else {
      navigate(`/recent/page/${page}`, { replace: true });
    }
    
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="recent-naats-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Recent Naats...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="recent-naats-page">
      <SEO
        title="Recent Naats - NaatCity"
        description="Discover the latest Islamic Naats added to NaatCity. Listen to newly uploaded naats from various Naat Khawans and stay updated with fresh spiritual content."
        keywords="recent naats, new naats, latest islamic naats, fresh naats, newly added naats"
        url="https://naatcity.com/recent"
      />
      <div className="page-header">
        <div className="header-content">
          <div className="header-center">
            <h1>
              <Clock size={32} />
              Recent Naats
            </h1>
            <div className="results-count">
              {(recentAudios && recentAudios.length > 0) ? (
                <>
                  Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, effectiveTotalCount)} of {effectiveTotalCount} Recent Naats
                </>
              ) : (
                'No Recent Naats Found'
              )}
            </div>
          </div>
          <div className="layout-toggle" role="group" aria-label="Change layout">
            <button
              type="button"
              className={`layout-btn ${layout === 'grid' ? 'active' : ''}`}
              aria-pressed={layout === 'grid'}
              onClick={() => handleLayoutChange('grid')}
            >
              <LayoutGrid size={16} /> <span className="visually-hidden">Grid view</span>
            </button>
            <button
              type="button"
              className={`layout-btn ${layout === 'list' ? 'active' : ''}`}
              aria-pressed={layout === 'list'}
              onClick={() => handleLayoutChange('list')}
            >
              <ListIcon size={16} /> <span className="visually-hidden">List view</span>
            </button>
          </div>
        </div>
      </div>

      <div className="content-section">
        {(recentAudios || []).length === 0 && !loading ? (
          <div className="empty-state">
            <Clock size={48} />
            <h3>No Recent Naats Found</h3>
            <p>There are currently no recent naats to display.</p>
          </div>
        ) : layout === 'list' ? (
          <div className="naat-card-list">
            {(currentPageData || []).map((audio, index) => {
              const isPlaying = state.currentAudio?.id === audio.id && state.isPlaying;
              return (
                <NaatCard
                  key={audio.id}
                  naat={audio}
                  serialNumber={startIndex + index + 1}
                  isPlaying={isPlaying}
                  isFavorite={isFavorite(audio.id)}
                  onPlay={handlePlayAudio}
                  onPause={() => togglePlay()}
                  onAddToQueue={handleAddToQueue}
                  onToggleFavorite={handleToggleFavorite}
                  onDownloadComplete={handleDownloadComplete}
                />
              );
            })}
          </div>
        ) : (
          <div className="naat-card-grid">
            {(currentPageData || []).map((audio, index) => {
              const isPlaying = state.currentAudio?.id === audio.id && state.isPlaying;
              return (
                <NaatCard
                  key={audio.id}
                  naat={audio}
                  serialNumber={startIndex + index + 1}
                  isPlaying={isPlaying}
                  isFavorite={isFavorite(audio.id)}
                  onPlay={handlePlayAudio}
                  onPause={() => togglePlay()}
                  onAddToQueue={handleAddToQueue}
                  onToggleFavorite={handleToggleFavorite}
                  onDownloadComplete={handleDownloadComplete}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination and Controls */}
      {TOTAL_PAGES > 1 && (
        <div className="bottom-controls">
          <div className="simple-pagination">
            {Array.from({ length: TOTAL_PAGES }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className={`page-btn ${currentPage === page ? 'active' : ''}`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}
          </div>
          {/* Page size control removed for SEO-friendly URLs */}
        </div>
      )}
    </div>
  );
};

export default RecentNaatsPage;
